import React from "react";
import { getFilterLabel } from "./getFilterLabel";

const TH_CLASS =
  "px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap";
const TD_CLASS = "px-3 sm:px-6 py-4 whitespace-nowrap text-xs md:text-sm";

export function getSourceBadgeColor(source) {
  switch (source) {
    case "revenuecat":
      return "bg-blue-100 text-blue-800";
    case "manual":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function getStatusBadgeColor(status) {
  if (!status) return "bg-gray-100 text-gray-800";
  const s = String(status).toUpperCase();
  switch (s) {
    case "INITIAL_PURCHASE":
      return "bg-emerald-100 text-emerald-800";
    case "RENEWAL":
      return "bg-green-100 text-green-800";
    case "CANCELLATION":
      return "bg-red-100 text-red-800";
    case "SUBSCRIPTION_EXTENDED":
      return "bg-sky-100 text-sky-800";
    case "PRODUCT_CHANGE":
      return "bg-violet-100 text-violet-800";
    case "MANUAL":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function getStatusLabel(status) {
  if (!status) return "-";
  const s = String(status).toUpperCase();
  const labels = {
    INITIAL_PURCHASE: "Initial Purchase",
    RENEWAL: "Renewal",
    CANCELLATION: "Cancellation",
    SUBSCRIPTION_EXTENDED: "Extended",
    PRODUCT_CHANGE: "Product Change",
    MANUAL: "Manual",
  };
  return labels[s] || status;
}

function formatDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SubscriptionHistoryTable({
  items,
  isLoading,
  error,
  onRetry,
}) {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center gap-3 text-red-600">
          <svg
            className="animate-spin h-6 w-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="font-medium text-sm md:text-lg">
            Loading subscription history...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-medium flex-1">{error}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-sm font-medium px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-lg transition-colors">
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!items?.length) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs md:text-sm text-gray-500">
          No subscription history found
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            <th className={TH_CLASS}>User</th>
            <th className={TH_CLASS}>Subscription</th>
            <th className={TH_CLASS}>Status</th>
            <th className={TH_CLASS}>Source</th>
            <th className={TH_CLASS}>Amount</th>
            <th className={TH_CLASS}>Currency</th>
            <th className={TH_CLASS}>Start Date</th>
            <th className={TH_CLASS}>End Date</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50 transition-colors">
              <td className={TD_CLASS}>
                <div className="text-xs md:text-sm font-medium text-gray-900">
                  {item.firstName} {item.lastName}
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[180px] sm:max-w-none">
                  {item.email}
                </div>
                <div className="text-xs text-gray-400">
                  ID: {item.user_id}
                </div>
              </td>
              <td className={TD_CLASS}>
                <span className="text-gray-900">
                  {item.subscription_name || `Plan ${item.subscription}`}
                </span>
                {item.duration && (
                  <span className="text-gray-500 text-xs block">
                    {item.duration}
                  </span>
                )}
              </td>
              <td className={TD_CLASS}>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                    item.status
                  )}`}>
                  {getStatusLabel(item.status)}
                </span>
              </td>
              <td className={TD_CLASS}>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceBadgeColor(
                    item.source
                  )}`}>
                  {getFilterLabel("source", item.source)}
                </span>
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {item.amount != null ? Number(item.amount) : "-"}
              </td>
              <td className={`${TD_CLASS} text-gray-600`}>
                {item.currency || "-"}
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {formatDate(item.start_date)}
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {formatDate(item.end_date)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
